// COMPONENT_SIZE_JUSTIFIED: Dialog with 5 sequential steps — splitting would fragment the flow without reducing complexity
// NEW_FILE_JUSTIFIED: Import Employee dialog — file upload + AI extraction + review form, no existing equivalent
import { useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Upload, Sparkles, AlertCircle, FileText, X, Play } from 'lucide-react';
import { useImportEmployee } from '@/hooks/useImportEmployee';
import { useImportEmployeeReviewForm } from '@/hooks/useImportEmployeeReviewForm';
import { cn } from '@/lib/utils';
import type { IImportEmployeeDialogProps, TDocumentSlot } from '@/@types/company';
import { isDocumentSlot } from '@/lib/type-guards';

const SLOT_LABELS: Record<TDocumentSlot, string> = {
  identityDocFront: 'ID / Passport — Front',
  identityDocBack: 'ID / Passport — Back',
  drivingLicenseFront: 'Driving Licence — Front',
  drivingLicenseBack: 'Driving Licence — Back',
};

function slotFromClassification(
  docType: 'identity' | 'driving_license',
  side: 'front' | 'back'
): TDocumentSlot {
  if (docType === 'driving_license') {
    return side === 'front' ? 'drivingLicenseFront' : 'drivingLicenseBack';
  }
  return side === 'front' ? 'identityDocFront' : 'identityDocBack';
}

export function ImportEmployeeDialog({
  open,
  onOpenChange,
  companyId,
}: IImportEmployeeDialogProps) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    step,
    uploadedFiles,
    pendingFileNames,
    extractedData,
    error,
    uploadFiles,
    saveEmployee,
    reset,
  } = useImportEmployee(companyId);

  const {
    name,
    dateOfBirth,
    identityDocumentNumber,
    nationality,
    city,
    postalCode,
    phoneNumber,
    email,
    documentAssignments,
    pendingFiles,
    setField,
    setDocumentSlot,
    addFiles,
    removeFile,
    resetForm,
  } = useImportEmployeeReviewForm();

  // Populate form when extraction completes
  useEffect(() => {
    if (step === 'review' && extractedData) {
      setField('name', extractedData.name);
      if (extractedData.dateOfBirth) setField('dateOfBirth', extractedData.dateOfBirth);
      if (extractedData.identityDocumentNumber)
        setField('identityDocumentNumber', extractedData.identityDocumentNumber);
      if (extractedData.nationality) setField('nationality', extractedData.nationality);
      if (extractedData.city) setField('city', extractedData.city);
      if (extractedData.postalCode) setField('postalCode', extractedData.postalCode);
      if (extractedData.phoneNumber) setField('phoneNumber', extractedData.phoneNumber);
      if (extractedData.email) setField('email', extractedData.email);

      // Auto-assign documents based on AI classification
      for (const doc of extractedData.documents) {
        const file = uploadedFiles[doc.fileIndex];
        if (!file) continue;
        const slot = slotFromClassification(doc.documentType, doc.side);
        setDocumentSlot(slot, { storageId: file.storageId, fileName: file.fileName });
      }
    }
  }, [step, extractedData, uploadedFiles, setField, setDocumentSlot]);

  const handleClose = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        reset();
        resetForm();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange, reset, resetForm]
  );

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      addFiles(Array.from(files));
    },
    [addFiles]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const handleStartRecognition = useCallback(() => {
    if (pendingFiles.length === 0) return;
    void uploadFiles(pendingFiles);
  }, [pendingFiles, uploadFiles]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) return;
    const employeeId = await saveEmployee({
      name: name.trim(),
      dateOfBirth: dateOfBirth || undefined,
      identityDocumentNumber: identityDocumentNumber || undefined,
      nationality: nationality || undefined,
      city: city || undefined,
      postalCode: postalCode || undefined,
      phoneNumber: phoneNumber || undefined,
      email: email || undefined,
      documentAssignments,
    });
    if (employeeId) {
      handleClose(false);
      navigate(`/company/employees/${employeeId}`);
    }
  }, [
    name,
    dateOfBirth,
    identityDocumentNumber,
    nationality,
    city,
    postalCode,
    phoneNumber,
    email,
    documentAssignments,
    saveEmployee,
    handleClose,
    navigate,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" aria-hidden="true" />
            Import Employee
          </DialogTitle>
          <DialogDescription>
            Upload identity documents to extract employee information with AI.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="flex flex-col gap-4">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              onChange={e => {
                handleFileSelect(e.target.files);
                e.target.value = '';
              }}
            />
            <div
              role="button"
              tabIndex={0}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              className={cn(
                'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border',
                'bg-card py-8 px-6 text-center cursor-pointer hover:bg-muted/50 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
              )}
            >
              <Upload className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Click or drag files to upload
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, JPG, PNG — up to 3 files (front/back of ID, licence)
                </p>
              </div>
            </div>

            {pendingFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground">
                  Selected files ({pendingFiles.length}/3)
                </p>
                {pendingFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}`}
                    className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText
                        className="w-4 h-4 text-muted-foreground shrink-0"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-foreground truncate">{file.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {(file.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0"
                      aria-label={`Remove ${file.name}`}
                      onClick={e => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                    >
                      <X className="w-3.5 h-3.5" aria-hidden="true" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleStartRecognition}
                disabled={pendingFiles.length === 0 || pendingFiles.length > 3}
              >
                <Play className="w-4 h-4 mr-2" aria-hidden="true" />
                Start Recognition
              </Button>
            </div>
          </div>
        )}

        {step === 'extracting' && (
          <div className="flex flex-col gap-4" role="status" aria-live="polite" aria-busy="true">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="w-4 h-4 animate-pulse" aria-hidden="true" />
              <span>Analyzing documents...</span>
            </div>
            {pendingFileNames.map(fname => (
              <div key={fname} className="flex items-center gap-2 text-xs text-muted-foreground">
                <FileText className="w-3.5 h-3.5" aria-hidden="true" />
                <span className="truncate">{fname}</span>
              </div>
            ))}
            <div className="space-y-3">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-3/4" />
            </div>
          </div>
        )}

        {step === 'review' && extractedData && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" aria-hidden="true" />
                Imported with AI
              </Badge>
              <span className="text-xs text-muted-foreground">
                Review and confirm before saving
              </span>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="import-emp-name" className="text-[13px] font-medium text-foreground">
                  Full Name{' '}
                  <span className="text-destructive" aria-hidden="true">*</span>
                  <span className="sr-only">(required)</span>
                </label>
                <Input
                  id="import-emp-name"
                  value={name}
                  onChange={e => setField('name', e.target.value)}
                  aria-required="true"
                  className="!h-10"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="import-emp-dob" className="text-[13px] font-medium text-foreground">
                    Date of Birth
                  </label>
                  <Input
                    id="import-emp-dob"
                    type="date"
                    value={dateOfBirth}
                    onChange={e => setField('dateOfBirth', e.target.value)}
                    className="!h-10"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="import-emp-nationality"
                    className="text-[13px] font-medium text-foreground"
                  >
                    Nationality
                  </label>
                  <Input
                    id="import-emp-nationality"
                    value={nationality}
                    onChange={e => setField('nationality', e.target.value)}
                    className="!h-10"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="import-emp-idnum"
                    className="text-[13px] font-medium text-foreground"
                  >
                    ID / Passport Number
                  </label>
                  <Input
                    id="import-emp-idnum"
                    value={identityDocumentNumber}
                    onChange={e => setField('identityDocumentNumber', e.target.value)}
                    className="!h-10"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="import-emp-city" className="text-[13px] font-medium text-foreground">
                    City
                  </label>
                  <Input
                    id="import-emp-city"
                    value={city}
                    onChange={e => setField('city', e.target.value)}
                    className="!h-10"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="import-emp-postal"
                    className="text-[13px] font-medium text-foreground"
                  >
                    Postal Code
                  </label>
                  <Input
                    id="import-emp-postal"
                    value={postalCode}
                    onChange={e => setField('postalCode', e.target.value)}
                    className="!h-10"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="import-emp-phone"
                    className="text-[13px] font-medium text-foreground"
                  >
                    Phone
                  </label>
                  <Input
                    id="import-emp-phone"
                    value={phoneNumber}
                    onChange={e => setField('phoneNumber', e.target.value)}
                    className="!h-10"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="import-emp-email" className="text-[13px] font-medium text-foreground">
                  Email
                </label>
                <Input
                  id="import-emp-email"
                  type="email"
                  value={email}
                  onChange={e => setField('email', e.target.value)}
                  className="!h-10"
                />
              </div>

              {uploadedFiles.length > 0 && (
                <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                  <p className="text-xs font-medium text-foreground">Document Assignments</p>
                  {uploadedFiles.map((file, index) => {
                    const classification = extractedData.documents.find(
                      d => d.fileIndex === index
                    );
                    const currentSlot = classification
                      ? slotFromClassification(classification.documentType, classification.side)
                      : 'identityDocFront';

                    return (
                      <div
                        key={file.storageId}
                        className="flex items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText
                            className="w-3.5 h-3.5 text-muted-foreground shrink-0"
                            aria-hidden="true"
                          />
                          <span className="text-xs text-foreground truncate">
                            {file.fileName}
                          </span>
                        </div>
                        <Select
                          defaultValue={currentSlot}
                          aria-label={`Document slot for ${file.fileName}`}
                          onValueChange={(val: string) => {
                            if (!isDocumentSlot(val)) return;
                            setDocumentSlot(val, {
                              storageId: file.storageId,
                              fileName: file.fileName,
                            });
                          }}
                        >
                          <SelectTrigger className="!h-8 text-xs w-44 shrink-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(SLOT_LABELS).map(([key, label]) => (
                              <SelectItem key={key} value={key}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={() => void handleSave()} disabled={!name.trim()}>
                Save Employee
              </Button>
            </div>
          </div>
        )}

        {step === 'saving' && (
          <div className="flex flex-col gap-3" role="status" aria-live="polite" aria-busy="true">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="w-4 h-4 animate-pulse" aria-hidden="true" />
              <span>Saving employee...</span>
            </div>
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {step === 'error' && (
          <div className="flex flex-col gap-4">
            <div role="alert" className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-3">
              <AlertCircle
                className="w-4 h-4 text-destructive shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-sm text-destructive">{error ?? 'An unexpected error occurred'}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => handleClose(false)}>
                Close
              </Button>
              <Button onClick={reset}>Try Again</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
